import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { ChevronDownIcon, ChevronUpIcon, CopyIcon, GripVerticalIcon, PencilIcon, Share2Icon, XIcon } from 'lucide-react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { ViewHeader } from '../components/ViewHeader';
import { EmptyState } from '../components/EmptyState';
import { useWorkspace } from '../hooks/useWorkspace';
import { useLang } from '../hooks/useLang';
import { useCatalogue } from '../hooks/useCatalogues';
import { useToast } from '../components/Toast';
import { catalogueKeyFor, resolveProgram } from '../../data/catalogue';
import { MAX_EXERCISES } from '../../domain/constants';
import { duplicateNumbers, newProgramme, programmeLabel, totalPoints, uid } from '../../domain/workspace';
import { checkProgram, programLimits } from '../../domain/rules';
import type { ResolvedProgram } from '../../domain/rules';
import { encodeProgrammeShare } from '../../domain/share';
import { deNumber } from '../../lib/format';
import { copyText } from '../../lib/download';
import { t } from '../../i18n';
import { ageTierLabel, figureVariantNames, starterTypeLabel } from '../../i18n/labels';
import { wosGlyph } from '../wos';
import type { FigurePickerHandle } from './FigurePicker';
import type { WorkspaceStore } from '../../domain/store';
import type { AgeTier, Exercise, FigureVariant, Programme, StarterType } from '../../types';

const TYPE_OPTIONS: readonly StarterType[] = ['single', 'pair', 'team4', 'team6'];
const TIER_OPTIONS: readonly AgeTier[] = ['eliteJunior', 'youth'];

export interface ProgrammeViewHandle {
  openNewProgrammeForm(): void;
  selectProgramme(id: string): void;
}

/** `programm.duplicate` deliberately embeds a `<b>` tag (see de.ts) — the
 *  vanilla version rendered it via `innerHTML` after escaping the figure
 *  list first; this does the same for the JSX port. */
function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function limitFor(p: Programme): number {
  return programLimits(p)?.maxTotal ?? MAX_EXERCISES;
}

/**
 * The programme editor — the highest-risk view in the React migration:
 * drag-and-drop reorder (now via `@dnd-kit/sortable`, gaining touch support
 * the vanilla native-HTML5-DnD version never had — the ▲▼ buttons stay as
 * an always-available fallback), live 8.2.002/8.2.011/8.2.028 rule warnings,
 * the difficulty-profile bar chart,
 * and the way-of-stretch glyphs. The latter two are preserved byte-for-byte
 * (same height math, `wosGlyph` untouched and wrapped via
 * `dangerouslySetInnerHTML`) since they're deliberately not in scope for
 * any visual redesign.
 */
export const ProgrammeView = forwardRef<ProgrammeViewHandle, { store: WorkspaceStore; picker: RefObject<FigurePickerHandle | null>; hidden: boolean }>(
  ({ store, picker, hidden }, ref) => {
    const workspace = useWorkspace(store);
    useLang();
    const toast = useToast();

    const [creating, setCreating] = useState(false);
    const [newType, setNewType] = useState<StarterType>('single');
    const [newTier, setNewTier] = useState<AgeTier>('eliteJunior');
    const [editingName, setEditingName] = useState(false);

    const newNameRef = useRef<HTMLInputElement>(null);
    const nameInputRef = useRef<HTMLInputElement>(null);

    // Stable per-exercise ids for @dnd-kit/sortable — `Exercise` has no
    // unique id of its own (two entries can legitimately share the same
    // figure number, see `dupes` below), so identity is tracked by object
    // reference instead, assigned once per object and stable across
    // reorders (which mutate array position, never replace the objects).
    const idsRef = useRef(new WeakMap<Exercise, string>());
    const idCounterRef = useRef(0);
    function stableId(ex: Exercise): string {
      let id = idsRef.current.get(ex);
      if (id === undefined) {
        id = `ex-${idCounterRef.current++}`;
        idsRef.current.set(ex, id);
      }
      return id;
    }

    const p = workspace.programs.find((x) => x.id === workspace.activeProgramId) ?? workspace.programs[0] ?? null;
    const catalogue = useCatalogue(p ? catalogueKeyFor(p.type) : null);

    useImperativeHandle(
      ref,
      () => ({
        openNewProgrammeForm() {
          setCreating(true);
        },
        selectProgramme(id) {
          store.update((proj) => {
            proj.activeProgramId = id;
          });
          setCreating(false);
        },
      }),
      [store],
    );

    // Keep `activeProgramId` pointing at a real programme once one exists —
    // a side effect, so it belongs in an effect rather than during render.
    useEffect(() => {
      if (p && workspace.activeProgramId !== p.id) {
        store.workspace.activeProgramId = p.id;
        store.touch();
      }
    }, [p, workspace.activeProgramId, store]);

    useEffect(() => {
      if (editingName) nameInputRef.current?.focus();
    }, [editingName]);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

    function move(prog: Programme, from: number, delta: number): void {
      const to = from + delta;
      if (to < 0 || to >= prog.program.length) return;
      store.update(() => {
        const [item] = prog.program.splice(from, 1);
        if (item) prog.program.splice(to, 0, item);
      });
    }

    function handleAddExercise(prog: Programme): void {
      void picker.current!.open(prog, catalogueKeyFor(prog.type), (ex: Exercise) => {
        store.update(() => prog.program.push(ex));
      });
    }

    function handleCreateProgramme(): void {
      const name = newNameRef.current?.value.trim() ?? '';
      const programme = { ...newProgramme(newType, newTier), name };
      store.update((proj) => {
        proj.programs.push(programme);
        proj.activeProgramId = programme.id;
      });
      setCreating(false);
    }

    function commitName(prog: Programme, value: string): void {
      if (!editingName) return; // guards a stale blur-then-Enter (or vice versa) double-commit
      const next = value.trim();
      store.update(() => {
        prog.name = next;
      });
      setEditingName(false);
    }

    function handleDuplicate(prog: Programme): void {
      const label = programmeLabel(prog, starterTypeLabel(prog.type), ageTierLabel(prog.ageTier));
      const copy: Programme = {
        ...structuredClone(prog),
        id: uid(),
        name: t('programme.duplicateName', { name: label }),
      };
      store.update((proj) => {
        proj.programs.push(copy);
        proj.activeProgramId = copy.id;
      });
      toast(t('programme.duplicated'));
    }

    function handleRemove(prog: Programme, index: number): void {
      store.update(() => prog.program.splice(index, 1));
    }

    function handleClear(prog: Programme): void {
      if (!confirm(t('programme.clearConfirm'))) return;
      store.update(() => {
        prog.program = [];
      });
      toast(t('programme.cleared'));
    }

    function handleShare(prog: Programme): void {
      const url = `${location.origin}${location.pathname}#${encodeProgrammeShare(prog)}`;
      if (typeof navigator.share === 'function') {
        // Ignore rejection: it's almost always the user cancelling the share
        // sheet, which is not a failure worth falling back for.
        navigator.share({ url }).catch(() => {});
        return;
      }
      void copyText(url).then(toast);
    }

    function handleDeleteProgramme(prog: Programme): void {
      const used = store.workspace.entries.filter((e) => e.programId === prog.id).length;
      if (used > 0 && !confirm(t('programme.deleteConfirm', { used }))) return;
      store.update((proj) => {
        proj.programs = proj.programs.filter((x) => x.id !== prog.id);
        // An Entry without its programme has nothing to export — drop it
        // along with the programme rather than leave a dangling reference.
        proj.entries = proj.entries.filter((e) => e.programId !== prog.id);
        proj.activeProgramId = proj.programs[0]?.id ?? null;
      });
      toast(t('programme.deleted'));
    }

    function handleDragEnd(prog: Programme, event: DragEndEvent): void {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const fromIndex = prog.program.findIndex((ex) => stableId(ex) === active.id);
      const toIndex = prog.program.findIndex((ex) => stableId(ex) === over.id);
      if (fromIndex === -1 || toIndex === -1) return;
      store.update(() => {
        const [item] = prog.program.splice(fromIndex, 1);
        if (item) prog.program.splice(toIndex, 0, item);
      });
    }

    if (workspace.programs.length === 0 || creating) {
      return (
        <section className="mx-auto flex max-w-[1180px] flex-col gap-3 p-4" hidden={hidden}>
          <ViewHeader title={t('programme.title')} />
          {workspace.programs.length === 0 && !creating ? (
            <EmptyState
              title={t('programme.empty.title')}
              text={t('programme.empty.text')}
              action={<Button onClick={() => setCreating(true)}>{t('programme.new.button')}</Button>}
            />
          ) : (
            <Card className="ring-2 ring-ring/40">
              <CardHeader>
                <CardTitle>{t('programme.new.title')}</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="programm-new-type">{t('programme.new.type')}</Label>
                  <Select value={newType} onValueChange={(v) => setNewType(v as StarterType)}>
                    <SelectTrigger id="programm-new-type" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TYPE_OPTIONS.map((k) => (
                        <SelectItem key={k} value={k}>
                          {starterTypeLabel(k)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="programm-new-tier">{t('programme.new.tier')}</Label>
                  <Select value={newTier} onValueChange={(v) => setNewTier(v as AgeTier)}>
                    <SelectTrigger id="programm-new-tier" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIER_OPTIONS.map((k) => (
                        <SelectItem key={k} value={k}>
                          {ageTierLabel(k)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-full flex flex-col gap-1.5">
                  <Label htmlFor="programm-new-name">
                    <span>{t('programme.new.name')}</span>
                    <span className="font-mono text-[10px] font-normal text-muted-foreground">{t('common.optional')}</span>
                  </Label>
                  <Input id="programm-new-name" ref={newNameRef} type="text" placeholder={t('programme.new.namePlaceholder')} />
                </div>
              </CardContent>
              <CardFooter>
                <Button onClick={handleCreateProgramme}>{t('programme.new.create')}</Button>
                {workspace.programs.length > 0 ? (
                  <Button variant="outline" onClick={() => setCreating(false)}>
                    {t('common.cancel')}
                  </Button>
                ) : null}
              </CardFooter>
            </Card>
          )}
        </section>
      );
    }

    if (!p) return null;

    const resolved: ResolvedProgram = catalogue ? resolveProgram(catalogue, p.program) : p.program.map(() => undefined);
    const loading = !catalogue && p.program.length > 0;
    const points = totalPoints(resolved);
    const count = p.program.length;
    const limit = limitFor(p);
    const peak = Math.max(...resolved.map((v) => v?.points ?? 0), 1);
    const dupes = new Set(duplicateNumbers(p.program));
    const ruleIssues = catalogue ? checkProgram(p, resolved) : [];
    const ids = p.program.map(stableId);

    let lastBikes: 1 | 2 | undefined;

    return (
      <section className="mx-auto flex max-w-[1180px] flex-col gap-3 p-4" hidden={hidden}>
        <header className="flex flex-wrap items-center gap-1.5">
          {editingName ? (
            <input
              ref={nameInputRef}
              className="min-w-0 flex-1 rounded-md border border-ring px-2 py-1 text-2xl font-bold tracking-tight outline-none"
              defaultValue={p.name}
              placeholder={programmeLabel(p, starterTypeLabel(p.type), ageTierLabel(p.ageTier))}
              aria-label={t('programme.editName')}
              onBlur={(ev) => commitName(p, ev.target.value)}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter') {
                  ev.preventDefault();
                  commitName(p, ev.currentTarget.value);
                }
                if (ev.key === 'Escape') setEditingName(false);
              }}
            />
          ) : (
            <h2 className="text-2xl font-bold tracking-tight text-foreground">
              {t('programme.titleWithName', { name: programmeLabel(p, starterTypeLabel(p.type), ageTierLabel(p.ageTier)) })}
            </h2>
          )}
          <Button variant="ghost" size="icon" aria-label={t('programme.editName')} title={t('programme.editName')} onClick={() => setEditingName(true)}>
            <PencilIcon className="size-4" />
          </Button>
          <Button variant="ghost" size="icon" disabled={count === 0} aria-label={t('programme.share')} title={t('programme.share')} onClick={() => handleShare(p)}>
            <Share2Icon className="size-4" />
          </Button>
        </header>

        <div className="flex flex-wrap items-end gap-4 rounded-lg bg-primary px-4 py-3.5 text-primary-foreground">
          <div>
            <p className="font-mono text-[9.5px] font-medium tracking-widest text-primary-foreground/55 uppercase">{t('programme.readout.aufgestellt')}</p>
            <p className="font-mono text-2xl leading-none font-semibold tracking-tight">{deNumber(points)}</p>
          </div>
          <div className="h-8 w-px self-center bg-white/15" />
          <div>
            <p className="font-mono text-[9.5px] font-medium tracking-widest text-primary-foreground/55 uppercase">{t('programme.readout.uebungen')}</p>
            <p className="font-mono text-lg leading-none font-medium">
              {count}
              <span className="text-primary-foreground/55">/{limit}</span>
            </p>
          </div>
          <div className="h-8 w-px self-center bg-white/15" />
          <div className="min-w-32 flex-1">
            <p className="font-mono text-[9.5px] font-medium tracking-widest text-primary-foreground/55 uppercase">{t('programme.readout.profil')}</p>
            <div className="flex h-8.5 items-end gap-0.5">
              {count > 0 ? (
                p.program.map((u, i) => {
                  const pts = resolved[i]?.points ?? 0;
                  return (
                    <span
                      key={stableId(u) + i}
                      className={cn('min-h-0.5 flex-1 rounded-[1px] bg-[#4e6b90]', pts >= peak * 0.85 && 'bg-[#e8636d]')}
                      style={{ height: Math.max(6, Math.round((pts / peak) * 34)) }}
                      title={`${u.nr} · ${deNumber(pts)}`}
                    />
                  );
                })
              ) : (
                <span className="font-mono text-[11px] text-primary-foreground/55">{t('programme.profileEmpty')}</span>
              )}
            </div>
          </div>
        </div>

        {dupes.size > 0 ? (
          <Alert variant="warning">
            <AlertDescription dangerouslySetInnerHTML={{ __html: t('programme.duplicate', { list: escapeHtml([...dupes].join(', ')) }) }} />
          </Alert>
        ) : null}

        {ruleIssues.map((issue, i) => (
          <Alert key={i} variant={issue.level === 'error' ? 'destructive' : 'warning'}>
            <AlertDescription>{issue.message}</AlertDescription>
          </Alert>
        ))}

        {loading ? (
          <p className="font-mono text-[11px] text-muted-foreground">{t('programme.catalogueLoading')}</p>
        ) : null}

        {count > 0 ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(ev) => handleDragEnd(p, ev)}>
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
              <ol className="overflow-hidden rounded-lg border bg-card">
                {p.program.map((u, i) => {
                  const variant = resolved[i];
                  let section: ReactNode = null;
                  if (p.type === 'pair' && variant?.bikes !== undefined && variant.bikes !== lastBikes) {
                    section = (
                      <li className="border-b bg-secondary/60 px-3 py-1.5 font-mono text-[10.5px] font-semibold tracking-widest text-muted-foreground uppercase">
                        {t(variant.bikes === 2 ? 'programme.bikes.two' : 'programme.bikes.one')}
                      </li>
                    );
                    lastBikes = variant.bikes;
                  }
                  return (
                    <ExerciseRow
                      key={stableId(u)}
                      id={stableId(u)}
                      sectionHeader={section}
                      nr={u.nr}
                      variant={variant}
                      index={i}
                      peak={peak}
                      isDup={dupes.has(u.nr)}
                      onMoveUp={() => move(p, i, -1)}
                      onMoveDown={() => move(p, i, 1)}
                      onRemove={() => handleRemove(p, i)}
                    />
                  );
                })}
              </ol>
            </SortableContext>
          </DndContext>
        ) : (
          <EmptyState title={t('programme.exercises.empty.title')} text={t('programme.exercises.empty.text', { label: starterTypeLabel(p.type) })} />
        )}

        <div className="flex flex-wrap gap-2">
          <Button disabled={count >= limit} onClick={() => handleAddExercise(p)}>
            {t('programme.addExercise')}
          </Button>
          {count > 0 ? (
            <Button variant="destructive" onClick={() => handleClear(p)}>
              {t('programme.clear')}
            </Button>
          ) : null}
          <Button variant="outline" disabled={count === 0} onClick={() => handleShare(p)}>
            {t('programme.share')}
          </Button>
          <Button variant="outline" onClick={() => handleDuplicate(p)}>
            <CopyIcon className="size-4" />
            {t('programme.duplicateProgramme')}
          </Button>
          <Button variant="destructive" onClick={() => handleDeleteProgramme(p)}>
            {t('programme.deleteProgramme')}
          </Button>
        </div>
      </section>
    );
  },
);
ProgrammeView.displayName = 'ProgrammeView';

function ExerciseRow({
  id,
  sectionHeader,
  nr,
  variant,
  index,
  peak,
  isDup,
  onMoveUp,
  onMoveDown,
  onRemove,
}: {
  id: string;
  sectionHeader: ReactNode;
  nr: string;
  /** The figure resolved from the catalogue, or `undefined` while the
   *  catalogue is still loading, or if the number no longer resolves to
   *  anything (a stale id) — either way, rendered as a bare placeholder row
   *  rather than guessed at. */
  variant: FigureVariant | undefined;
  index: number;
  peak: number;
  isDup: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } = useSortable({ id });

  const points = variant?.points ?? 0;
  const tactical =
    variant?.tactical && variant.tactical.length > 0 ? (
      <Badge variant="warn" title={t('programme.tactical.title')}>
        {t('programme.tactical.upTo', { points: deNumber(variant.tactical[variant.tactical.length - 1]![1]) })}
      </Badge>
    ) : null;
  const final = variant?.final ? (
    <Badge variant="info" title={t('programme.final.title')}>
      {t('programme.final.tag')}
    </Badge>
  ) : null;
  const names = variant ? figureVariantNames(variant) : null;

  return (
    <>
      {sectionHeader}
      <li
        ref={setNodeRef}
        className={cn(
          'grid grid-cols-[34px_1fr_auto] items-start gap-2.5 border-b px-3 py-2.5 last:border-b-0',
          isDup && 'bg-warn-soft',
          isDragging && 'opacity-40',
          isOver && 'shadow-[inset_0_2px_0_var(--color-brand)]',
        )}
        style={{ transform: CSS.Transform.toString(transform), transition: transition ?? undefined }}
      >
        <span className="flex justify-end gap-0.5 pt-0.5 font-mono text-xs font-medium text-muted-foreground">
          <span {...attributes} {...listeners} aria-label={t('programme.dragHandle')}>
            <GripVerticalIcon className="size-3.5 cursor-grab" aria-hidden="true" />
          </span>
          {index + 1}
        </span>
        <div className="min-w-0">
          <p className="flex flex-wrap items-baseline gap-1.5">
            <span className="rounded bg-accent px-1.5 py-px font-mono text-xs font-semibold text-accent-foreground">{nr}</span>
            {names ? (
              <span className="text-left text-sm font-medium">{names.primary}</span>
            ) : (
              <span className="text-left text-sm font-medium text-muted-foreground italic">{t('programme.figureNotFound')}</span>
            )}
          </p>
          {names ? <p className="mt-0.5 text-[11.5px] text-muted-foreground">{names.secondary}</p> : null}
          <p className="mt-1 flex flex-wrap items-center gap-1.5">
            <span dangerouslySetInnerHTML={{ __html: wosGlyph(variant?.wos) }} />
            {tactical}
            {final}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <span className="flex h-8.5 items-center gap-1.5">
            <span className="flex h-full w-1 items-end overflow-hidden rounded-full bg-secondary">
              <i className={cn('block w-full rounded-full bg-blue', points >= peak * 0.85 && 'bg-brand')} style={{ height: `${Math.round((points / peak) * 100)}%` }} />
            </span>
            <span className="w-8 shrink-0 text-right font-mono text-sm font-semibold">{deNumber(points)}</span>
          </span>
          <div className="flex flex-col gap-0.5">
            <Button variant="outline" size="icon" className="size-5" aria-label={t('programme.moveUp')} onClick={onMoveUp}>
              <ChevronUpIcon className="size-3" />
            </Button>
            <Button variant="outline" size="icon" className="size-5 text-destructive hover:bg-destructive/10" aria-label={t('programme.removeExercise')} onClick={onRemove}>
              <XIcon className="size-3" />
            </Button>
            <Button variant="outline" size="icon" className="size-5" aria-label={t('programme.moveDown')} onClick={onMoveDown}>
              <ChevronDownIcon className="size-3" />
            </Button>
          </div>
        </div>
      </li>
    </>
  );
}
